/**
 * Inline yield simulator — embeddable anywhere.
 *
 * Accepts an already-loaded `products` array so the host page
 * doesn't need an extra fetch. All computation is pure client-side
 * (simple interest per term: principal × rate × term_days / 365; a term's result joins the
 * capital the next term works with, because the next term starts by itself).
 */
import { useEffect, useMemo, useState } from 'react';
import { limitToCents } from '../lib/amount';
import { Link, useNavigate } from 'react-router-dom';
import type { ProductSummary } from '../services/portfolio.service';
import callbacksService from '../services/callbacks.service';
import contractsService from '../services/contracts.service';
import suitabilityService, {
  type SuitabilityCheckResult,
} from '../services/suitability.service';
import { useClientApprovalGate } from '../hooks/useCurrentUserProfile';
import { useMembershipApproved } from '../hooks/useMembershipApproved';
import MembershipFirstNote from './client/MembershipFirstNote';
import { getApiErrorCode, getApiErrorMessage } from '../lib/apiError';
import { channelsToExecutive, seriesCallContext } from '../lib/executiveRefusal';
import ExecutiveRefusal from './client/ExecutiveRefusal';
import { formatDate, formatMoney } from '../lib/datetime';
import {
  formatEarlyWithdrawal,
  formatManagementFee,
  formatRedemption,
  formatTerm,
} from '../lib/redemptionTerms';
import { colors, gradients } from '../styles/theme';
import RiskDisclaimer from './RiskDisclaimer';
import SimulatorProjection from './SimulatorProjection';
import { RISK_LEVELS } from '../lib/riskLevels';

// ─── Pure math ────────────────────────────────────────────────────

interface YieldResult {
  yieldAmount: number;
  totalReceived: number;
  annualRate: number;
  maturityDate: Date | null;
}

/** Term + redemption line through the shared derivation (lib/redemptionTerms
 *  — audit #138): never a liquidity promise the configured terms don't
 *  grant. */
function buildLockInText(selected: ProductSummary | null): string | null {
  if (!selected) return null;
  const redemption = formatRedemption(selected) ?? 'see series details for redemption terms';
  return `${formatTerm(selected)} · ${redemption}`;
}

/** How far ahead a person may look: about five years of whole terms (owner, 2026-10-25). */
export const SIMULATOR_HORIZON_DAYS = 5 * 365;

/** The quick amounts offered per currency. Dollar amounts start low, as the retail investing
 *  apps do; a quick amount below the Series' minimum is not offered. */
const QUICK_AMOUNTS: Record<string, readonly number[]> = {
  MXN: [50000, 100000, 250000, 500000],
  USD: [1000, 5000, 10000, 25000],
};

export function quickAmounts(currency: string, minimum: number): number[] {
  return (QUICK_AMOUNTS[currency] ?? QUICK_AMOUNTS.MXN).filter((n) => n >= minimum);
}

/** Whole terms that fit in the horizon: only a completed term earns its result, so a date
 *  inside a term is never offered. At least one. */
export function termsInHorizon(termDays: number | null | undefined): number {
  if (!termDays || termDays <= 0) return 1;
  return Math.max(1, Math.floor(SIMULATOR_HORIZON_DAYS / termDays));
}

export function computeYield(
  product: ProductSummary,
  amount: number,
  terms = 1,
  today: Date = new Date(),
): YieldResult | null {
  const rate = product.target_yield ? Number.parseFloat(product.target_yield) : null;
  if (rate == null || rate <= 0 || amount <= 0) return null;
  const termDays = product.term_days;
  let yieldAmount: number;
  let maturityDate: Date | null = null;
  if (termDays && termDays > 0) {
    const total = amount * (1 + (rate * termDays) / 365) ** terms;
    yieldAmount = total - amount;
    maturityDate = new Date(today);
    maturityDate.setDate(maturityDate.getDate() + termDays * terms);
  } else {
    yieldAmount = amount * rate;
  }
  return { yieldAmount, totalReceived: amount + yieldAmount, annualRate: rate * 100, maturityDate };
}

// ─── Sub-components ───────────────────────────────────────────────

interface ProductTabProps {
  readonly product: ProductSummary;
  readonly selected: boolean;
  readonly onClick: () => void;
}

function ProductTab({ product, selected, onClick }: ProductTabProps): JSX.Element {
  const rate = product.target_yield
    ? `${(Number.parseFloat(product.target_yield) * 100).toFixed(2)}%`
    : null;
  const term = product.term_days ? `${product.term_days}d` : 'Open';
  const risk = product.risk_level == null ? null : RISK_LEVELS[product.risk_level];

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ flexShrink: 0, padding: '10px 16px', borderRadius: 10, border: `2px solid ${selected ? colors.gold : colors.border}`, background: selected ? `${colors.gold}14` : colors.surface, cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, background 0.15s', minWidth: 140 }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: colors.ink, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }}>
        {product.name}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        {rate && <span style={{ fontSize: 17, fontWeight: 700, color: selected ? colors.gold : colors.navy }}>{rate}</span>}
        <span style={{ fontSize: 10, fontWeight: 500, color: colors.textMuted, background: colors.border, borderRadius: 3, padding: '1px 5px' }}>{term}</span>
        {risk && <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 3, padding: '1px 5px', color: risk.color, background: risk.bg }}>{risk.label}</span>}
      </div>
    </button>
  );
}

interface SimulatorResultProps {
  readonly result: YieldResult;
  readonly ccy: string;
  readonly product: ProductSummary;
  readonly terms: number;
  readonly onTerms: (terms: number) => void;
}

function TermEndChoice({ product, terms, onTerms }: Readonly<Pick<SimulatorResultProps, 'product' | 'terms' | 'onTerms'>>): JSX.Element {
  const termDays = product.term_days ?? 0;
  const today = new Date();
  const options = Array.from({ length: termsInHorizon(termDays) }, (_, i) => {
    const end = new Date(today);
    end.setDate(end.getDate() + termDays * (i + 1));
    return { terms: i + 1, label: formatDate(end) };
  });
  return (
    <select
      id="simulator-term-end"
      aria-label="Term ends"
      value={terms}
      onChange={(e) => onTerms(Number(e.target.value))}
      style={{ width: '100%', fontSize: 15, fontWeight: 600, background: 'transparent', color: '#fff', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      {options.map((o) => (
        <option key={o.terms} value={o.terms} style={{ color: colors.ink }}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function SimulatorResult({ result, ccy, product, terms, onTerms }: SimulatorResultProps): JSX.Element {
  const maturityLabel = result.maturityDate ? 'Term ends' : 'Per year';
  const maturityValue = result.maturityDate ? (
    <TermEndChoice product={product} terms={terms} onTerms={onTerms} />
  ) : (
    `${result.annualRate.toFixed(2)}%`
  );
  const heading = terms > 1 ? `Your result over ${terms} terms` : 'Your result for the term';

  return (
    <div style={{ background: gradients.navy, borderRadius: 12, padding: '18px 20px', color: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{heading}</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: colors.gold, lineHeight: 1 }}>{formatMoney(result.yieldAmount, ccy)}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 3 }}>Total</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{formatMoney(result.totalReceived, ccy)}</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 3 }}>{maturityLabel}</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{maturityValue}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Widget ───────────────────────────────────────────────────────

interface YieldSimulatorWidgetProps {
  readonly products: ProductSummary[];
  /** Pre-approval fit check (#58): whether the client's financial profile
   *  captures every suitability input (monthly_income, net_worth,
   *  investment_experience_years). `false` renders the "complete your
   *  profile" prompt; `true` enables the soft pre-check; `null`/omitted
   *  (still loading, or a non-dashboard mount) keeps the widget silent. */
  readonly fitProfileReady?: boolean | null;
  /** The widget IS the page (the Simulator page): the page shows the title, and the
   *  canonical disclaimer once at its own foot (audit #140), so the widget shows
   *  neither. */
  readonly standalone?: boolean;
}

export default function YieldSimulatorWidget({ products, fitProfileReady = null, standalone = false }: YieldSimulatorWidgetProps): JSX.Element | null {
  const fixedProducts = useMemo(
    () =>
      products.filter(
        (p) => p.terms_available && p.asset_class === 'fixed_income' && p.target_yield,
      ),
    [products],
  );

  const [selectedId, setSelectedId] = useState<string | null>(() => fixedProducts[0]?.id ?? null);
  const [rawAmount, setRawAmount] = useState<string>(() => fixedProducts[0]?.min_investment ?? '');
  const [terms, setTerms] = useState(1);

  const selected = useMemo(() => fixedProducts.find(p => p.id === selectedId) ?? null, [fixedProducts, selectedId]);

  // The products usually arrive after the first render: the first one is then selected,
  // at its minimum, so the simulator never opens blank.
  useEffect(() => {
    if (selected || fixedProducts.length === 0) return;
    setSelectedId(fixedProducts[0].id);
    setRawAmount(fixedProducts[0].min_investment ?? '');
  }, [selected, fixedProducts]);

  const amount = useMemo(() => {
    const n = Number.parseFloat(rawAmount.replace(/,/g, ''));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [rawAmount]);

  const result = useMemo(
    () => (selected ? computeYield(selected, amount, terms) : null),
    [selected, amount, terms],
  );

  const minInvest = selected?.min_investment ? Number.parseFloat(selected.min_investment) : 0;
  const belowMin = amount > 0 && amount < minInvest;
  const ccy = selected?.currency_code ?? 'MXN';
  const selectedRisk = selected?.risk_level == null ? null : RISK_LEVELS[selected.risk_level];
  const showResult = result !== null && !belowMin;

  const lockInText = buildLockInText(selected);
  const earlyWithdrawal = selected ? formatEarlyWithdrawal(selected) : null;

  const emptyText = belowMin
    ? 'Enter an amount above the minimum to see your result.'
    : 'Enter an amount to see your result.';

  // #58: the fit check runs post-approval always, and PRE-approval once the
  // client's financial profile carries every suitability input. Missing
  // inputs never reach the engine (they'd fail closed and read as a false
  // "not a fit") — the prompt chip below asks for the data instead.
  const gate = useClientApprovalGate();
  const preapprovalFitEnabled = gate !== 'approved' && fitProfileReady === true;
  const suitabilityByProduct = useProductSuitability(selected, gate, preapprovalFitEnabled);
  const suitability = selected ? suitabilityByProduct[selected.id] : undefined;
  const suitabilityBlocked = suitability !== undefined && !suitability.ok;

  if (fixedProducts.length === 0) return null;

  return (
    <section style={{ borderRadius: 16, overflow: 'hidden', border: `1px solid ${colors.border}`, marginBottom: 20 }}>
      {!standalone && (
        <div style={{ background: gradients.navy, padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Simulator</div>
        </div>
      )}

      <div style={{ background: colors.surface, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* Product tabs */}
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 2 }}>
          {fixedProducts.map(p => (
            <ProductTab
              key={p.id}
              product={p}
              selected={p.id === selectedId}
              onClick={() => { setSelectedId(p.id); setRawAmount(p.min_investment ?? ''); setTerms(1); }}
            />
          ))}
        </div>

        {/* #58 fit check — rendered ABOVE the amount so the verdict is
            visible before the client anchors on a number. Three states:
            incomplete profile (prompt), not-a-fit (soft warning), fit
            (quiet confirmation, pre-approval only). */}
        {gate !== 'approved' && fitProfileReady === false && (
          <FitProfilePrompt />
        )}
        {suitabilityBlocked && suitability && (
          <SuitabilityWarning suitability={suitability} />
        )}
        {preapprovalFitEnabled && suitability?.ok === true && (
          <p
            style={{
              fontSize: 11,
              margin: 0,
              padding: '8px 10px',
              borderRadius: 8,
              background: colors.successBg,
              color: colors.success,
            }}
          >
            ✓ Based on your profile so far, this series could be a fit. Your Account Executive will confirm the details with you.
          </p>
        )}

        {/* Amount + result row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, alignItems: 'start' }}>

          {/* Input side */}
          <div>
            <label
              htmlFor="simulator-amount"
              style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Contribution amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 600, color: colors.textMuted, pointerEvents: 'none' }}>
                {ccy}
              </span>
              <input
                id="simulator-amount"
                type="number"
                inputMode="decimal"
                value={rawAmount}
                min={minInvest > 0 ? minInvest : 0.01}
                step={ccy === 'USD' ? 100 : 1000}
                required
                onChange={e => setRawAmount(limitToCents(e.target.value))}
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 48px', fontSize: 18, fontWeight: 700, border: `2px solid ${belowMin ? colors.danger : colors.border}`, borderRadius: 10, outline: 'none', color: colors.ink, background: colors.surface }}
              />
            </div>
            {belowMin && (
              <p style={{ fontSize: 11, color: colors.danger, marginTop: 4 }}>
                Minimum is {formatMoney(minInvest, ccy)}
              </p>
            )}

            {/* Quick amounts */}
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {quickAmounts(ccy, minInvest).map(n => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRawAmount(String(n))}
                  style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, border: `1px solid ${amount === n ? colors.navy : colors.border}`, borderRadius: 6, background: amount === n ? colors.navy : colors.surface, color: amount === n ? '#fff' : colors.textMuted, cursor: 'pointer', transition: 'all 0.12s' }}
                >
                  {n >= 1000000 ? `${n / 1000000}M` : `${n / 1000}K`}
                </button>
              ))}
            </div>

            {/* T-02: lock-in + T-03: risk classification */}
            {selected && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>
                  {lockInText}
                  {' · '}{(Number.parseFloat(selected.target_yield ?? '0') * 100).toFixed(2)}% p.a.
                  {/* Audit #140: fee next to the yield it reduces. Only the
                      catalog / detail endpoints carry it — when the field is
                      absent (portfolio listing) the line is omitted, never
                      guessed. */}
                  {selected.management_fee_bps !== undefined && (
                    <>{' · '}Management fee: {formatManagementFee(selected)}</>
                  )}
                  {' · '}Min {formatMoney(minInvest, ccy)}
                </p>
                {earlyWithdrawal?.tone === 'danger' && (
                  <p style={{ fontSize: 11, color: colors.danger, margin: 0 }}>
                    Early withdrawal: {earlyWithdrawal.text}
                  </p>
                )}
                {selectedRisk && (
                  <p style={{ fontSize: 11, margin: 0 }}>
                    Risk: <span style={{ fontWeight: 700, color: selectedRisk.color }}>{selectedRisk.label}</span>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Result side */}
          {showResult && result && selected ? (
            <SimulatorResult result={result} ccy={ccy} product={selected} terms={terms} onTerms={setTerms} />
          ) : (
            <div style={{ border: `2px dashed ${colors.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 140, color: colors.textMuted, fontSize: 13, textAlign: 'center', padding: 16 }}>
              {emptyText}
            </div>
          )}
        </div>

        {/* Comparative growth projection — all products overlaid so
            the user can see at a glance how 90d vs 180d vs 365d
            compound the same investment amount. */}
        <SimulatorProjection
          products={fixedProducts}
          selectedId={selectedId}
          amount={amount}
          currency={ccy}
          horizonDays={(selected?.term_days ?? 0) * terms}
        />

        {/* Next-steps panel — replaces the single CTA. Primary action
            is consultative (talk to the advisor about THIS product);
            self-serve research and funding are secondary text links.
            The gate hook decides whether "How to fund" is shown — a
            not-yet-approved client can't fund, so we hide it. */}
        <NextStepsPanel selected={selected} gate={gate} suitability={suitability} />

        {/* The simulator's one informational label is the canonical disclaimer,
            one small muted line at its foot (owner, 2026-10-14; audit #140). */}
        {!standalone && <RiskDisclaimer compact />}
      </div>
    </section>
  );
}

// ─── Next steps panel ─────────────────────────────────────────────

interface NextStepsPanelProps {
  selected: ProductSummary | null;
  gate: string;
  suitability: SuitabilityCheckResult | undefined;
}

type CallbackUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'requested'; advisorName: string | null; alreadyExisted: boolean; assigned: boolean }
  | { kind: 'error'; message: string };

/** Human-readable advisor name from a callback result, or null if unknown. */
function formatAdvisorName(
  result: { advisor_first_name?: string | null; advisor_last_name?: string | null },
): string | null {
  if (!result.advisor_first_name && !result.advisor_last_name) return null;
  return `${result.advisor_first_name ?? ''} ${result.advisor_last_name ?? ''}`.trim();
}

/** Newline-joined description of a blocked suitability check, or undefined. */
function buildSuitabilityTooltip(result: SuitabilityCheckResult | undefined): string | undefined {
  if (!result || result.ok) return undefined;
  return result.missing_requirements
    .map((c) => suitabilityService.describeRequirement(c))
    .join('\n');
}

/** Distinguish the structured suitability rejection (#58) from generic
 *  network/server errors so the user sees actionable copy. */
function deriveInvestErrorMessage(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  const detail = (
    e as { response?: { data?: { detail?: { code?: string; missing_requirements?: string[] } } } }
  )?.response?.data?.detail;
  if (status === 403 && detail?.code === 'product_unsuitable') {
    const reasons = (detail.missing_requirements ?? [])
      .map((c) => suitabilityService.describeRequirement(c))
      .join(' · ');
    return reasons
      ? `This series isn't a fit yet — ${reasons.toLowerCase()}.`
      : "This series isn't a fit for your profile yet.";
  }
  return getApiErrorMessage(e, "Couldn't open the contract right now. Please try again.");
}

/** Suitability gate (#58) cached per product id — fires once per product on
 *  tab change. Runs for approved clients always, and for PRE-approval
 *  clients when the caller confirms the profile carries every suitability
 *  input (`preapprovalEnabled`); fail-open (leaves the entry undefined). */
function useProductSuitability(
  selected: { id: string } | null,
  gate: string,
  preapprovalEnabled = false,
): Record<string, SuitabilityCheckResult> {
  const [byProduct, setByProduct] = useState<Record<string, SuitabilityCheckResult>>({});
  useEffect(() => {
    if (!selected || (gate !== 'approved' && !preapprovalEnabled)) return;
    if (byProduct[selected.id] !== undefined) return;
    let cancelled = false;
    suitabilityService
      .checkProduct(selected.id)
      .then((r) => {
        if (!cancelled) setByProduct((prev) => ({ ...prev, [selected.id]: r }));
      })
      .catch(() => { /* leave undefined — fail-open in UI */ });
    return () => { cancelled = true; };
  }, [selected, gate, preapprovalEnabled, byProduct]);
  return byProduct;
}

/** #58 intermediate state: the fit check can't run because the financial
 *  profile is missing at least one suitability input. Invites completion —
 *  a missing value must read as "tell us more", never as a rejection. */
function FitProfilePrompt(): JSX.Element {
  return (
    <div
      style={{
        fontSize: 11,
        padding: '8px 10px',
        borderRadius: 8,
        background: colors.infoBg,
        color: colors.navy,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <span>
        Complete your financial profile to see whether this series fits you.
      </span>
      <Link
        to="/client/onboarding"
        style={{ color: colors.navy, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}
      >
        Complete profile →
      </Link>
    </div>
  );
}

/** Why-not-a-fit panel (#58): the failed suitability requirements + an
 *  optional suggested alternative product. */
function SuitabilityWarning({ suitability }: { readonly suitability: SuitabilityCheckResult }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 11,
        padding: '8px 10px',
        borderRadius: 8,
        background: colors.warningBg,
        color: colors.warning,
        border: `1px solid ${colors.warningBg}`,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>Why this series isn&apos;t a fit yet:</p>
      <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
        {suitability.missing_requirements.map((code) => (
          <li key={code}>{suitabilityService.describeRequirement(code)}</li>
        ))}
      </ul>
      {suitability.suggested_alternative_product_id && (
        <p style={{ margin: '6px 0 0' }}>
          You might consider{' '}
          <Link
            to={`/client/products/${suitability.suggested_alternative_product_id}`}
            style={{ color: colors.navy, textDecoration: 'underline' }}
          >
            {suitability.suggested_alternative_product_name ?? 'a different series'}
          </Link>
          {' '}instead.
        </p>
      )}
    </div>
  );
}

/** Primary "Talk to your advisor" CTA — copy + style follow the per-product
 *  callback state (idle / submitting / requested / error). */
function AdvisorCallbackButton({
  productState,
  selectedName,
  onClick,
}: {
  readonly productState: CallbackUiState;
  readonly selectedName: string;
  readonly onClick: () => void;
}): JSX.Element {
  const isRequested = productState.kind === 'requested';
  const isSubmitting = productState.kind === 'submitting';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isSubmitting || isRequested}
      style={{
        padding: '12px 18px',
        borderRadius: 10,
        border: 'none',
        fontSize: 14,
        fontWeight: 700,
        cursor: isSubmitting || isRequested ? 'default' : 'pointer',
        background: isRequested ? colors.successBg : gradients.gold,
        color: isRequested ? colors.success : colors.navy,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transition: 'background 0.2s',
      }}
    >
      {isSubmitting && <span>Requesting…</span>}
      {productState.kind === 'idle' && (
        <span>Talk to your Account Executive about {selectedName} →</span>
      )}
      {isRequested && (
        <span>
          {productState.assigned ? (
            <>
              ✓ {productState.alreadyExisted ? 'Your Account Executive already has this request' : 'Your Account Executive has been notified'}
              {productState.advisorName && !productState.alreadyExisted ? ` — ${productState.advisorName} will reach out` : ''}
            </>
          ) : (
            <>✓ Got it — we&apos;re connecting you with an Account Executive who&apos;ll reach out</>
          )}
        </span>
      )}
      {productState.kind === 'error' && (
        <span>Try again</span>
      )}
    </button>
  );
}

function NextStepsPanel({ selected, gate, suitability }: Readonly<NextStepsPanelProps>): JSX.Element | null {
  const navigate = useNavigate();
  // Per-product UI state — keyed by id so toggling tabs doesn't reset
  // the confirmation for a product the user already raised hand on.
  const [stateByProduct, setStateByProduct] = useState<Record<string, CallbackUiState>>({});
  // Track which product the user is currently spinning up a contract
  // case for so we can disable the button without blocking the others.
  const [investingProductId, setInvestingProductId] = useState<string | null>(null);
  const [investError, setInvestError] = useState<string | null>(null);
  // One that sends the client to their Account Executive is shown with the call (S-43).
  const [investErrorCode, setInvestErrorCode] = useState<string | null>(null);
  const membershipApproved = useMembershipApproved(gate === 'approved');
  if (!selected) return null;

  const productState: CallbackUiState = stateByProduct[selected.id] ?? { kind: 'idle' };
  // "Invest in this product" only appears when:
  //   1. The product has an active published per-product contract
  //      template (someone CAN sign something), AND
  //   2. The client is post-KYC-approved. Pre-approval clients route
  //      through "Talk to your advisor" — starting a product contract
  //      before base KYC clears mixes the funnels and would surface a
  //      confusing contract case in their /client/contracts list.
  const canInvest =
    !!selected.contract_template_version_id && gate === 'approved';
  const isInvesting = investingProductId === selected.id;
  const suitabilityBlocked = suitability !== undefined && !suitability.ok;
  const suitabilityTooltip = buildSuitabilityTooltip(suitability);

  async function handleRequestCallback(): Promise<void> {
    if (!selected || productState.kind === 'submitting') return;
    const productId = selected.id;
    setStateByProduct((prev) => ({ ...prev, [productId]: { kind: 'submitting' } }));
    try {
      const result = await callbacksService.requestCallback({
        product_id: productId,
        context_note: `From yield simulator. Series: ${selected.name}.`,
      });
      const advisorName = formatAdvisorName(result);
      setStateByProduct((prev) => ({
        ...prev,
        [productId]: {
          kind: 'requested',
          advisorName,
          alreadyExisted: result.already_existed,
          assigned: result.assigned,
        },
      }));
    } catch (e: unknown) {
      setStateByProduct((prev) => ({
        ...prev,
        [productId]: {
          kind: 'error',
          message: getApiErrorMessage(e, "Couldn't reach your Account Executive right now. Please try again."),
        },
      }));
    }
  }

  async function handleInvest(): Promise<void> {
    if (!selected?.contract_template_version_id || isInvesting) return;
    setInvestError(null);
    setInvestErrorCode(null);
    setInvestingProductId(selected.id);
    try {
      // `startCase` is idempotent server-side: an existing in-flight
      // contract case for the same (user, template_version) returns
      // that case_id rather than spawning a duplicate. So this single
      // path covers "start a new one" and "resume my existing one".
      const { case_id } = await contractsService.startCase(
        selected.contract_template_version_id,
      );
      navigate(`/client/contracts/${case_id}`);
    } catch (e: unknown) {
      setInvestErrorCode(getApiErrorCode(e));
      setInvestError(deriveInvestErrorMessage(e));
      setInvestingProductId(null);
    }
  }

  // Approved clients see "How to fund"; everyone else doesn't (they
  // can't fund yet, and the gate would just bounce them on click).
  const showFundLink = gate === 'approved';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Primary: Talk to your advisor (contextual to selected product) */}
      <AdvisorCallbackButton
        productState={productState}
        selectedName={selected.name}
        onClick={handleRequestCallback}
      />

      {productState.kind === 'error' && (
        <p style={{ fontSize: 11, color: colors.danger, margin: 0 }}>
          {productState.message}
        </p>
      )}

      {/* Secondary CTA: Invest in this product — only when there's an
          active per-product contract template AND the client has
          cleared base KYC. GitLab #58: disabled with tooltip if the
          per-product suitability check returned not-ok. Server-side
          gate still runs on click as defence in depth. Idempotent
          server-side so duplicate clicks can't spawn a second case. */}
      {canInvest && membershipApproved === false && <MembershipFirstNote />}

      {canInvest && membershipApproved !== false && (
        <button
          type="button"
          onClick={handleInvest}
          disabled={isInvesting || suitabilityBlocked}
          title={suitabilityTooltip}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: `2px solid ${suitabilityBlocked ? colors.border : colors.navy}`,
            fontSize: 13,
            fontWeight: 700,
            cursor: isInvesting || suitabilityBlocked ? 'not-allowed' : 'pointer',
            background: colors.surface,
            color: suitabilityBlocked ? colors.muted : colors.navy,
            opacity: suitabilityBlocked ? 0.7 : 1,
          }}
        >
          {isInvesting && 'Opening contract…'}
          {!isInvesting && suitabilityBlocked && 'Not a fit yet — talk to your Account Executive'}
          {!isInvesting && !suitabilityBlocked && `Contribute to ${selected.name} →`}
        </button>
      )}

      {investError && channelsToExecutive(investErrorCode) && (
        <ExecutiveRefusal
          message={investError}
          productId={selected.id}
          context={seriesCallContext('From the simulator', selected.name)}
        />
      )}
      {investError && !channelsToExecutive(investErrorCode) && (
        <p style={{ fontSize: 11, color: colors.danger, margin: 0 }}>
          {investError}
        </p>
      )}

      {/* Tertiary: text links — research and (conditionally) fund */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Link
          to={`/client/products/${selected.id}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: colors.navy,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          View series details
        </Link>
        {showFundLink && (
          <>
            <span style={{ fontSize: 12, color: colors.border }}>·</span>
            <Link
              to="/client/fund"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textMuted,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              How to make a contribution
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
